import { useEffect } from 'react';
import Smartlook from 'smartlook-client';
import { shouldNavigateToFinance } from 'utils/navigation';
import { useSession } from 'session';
import * as R from 'Router/routes';
import { Navigate, useLocation } from 'react-router';
import { useApplicationQuery, useClientQuery } from 'hooks/webapi';
import { ClientApplication } from 'hooks/webapi/client/useApplicationQuery';
import { Client } from 'hooks/webapi/client/useClientQuery';

type Props = {
  children: JSX.Element;
};

// These routes are valid before any application exists (e.g. landing pages, waiting screens)
const ROUTES_APPLICATION_NOT_NEEDED = [
  R.LOGIN,
  R.INTRO,
  R.INTRO_START_APP,
  R.INTRO_2,
  R.WELCOME_BACK_NEW,
  R.WAITING,
  R.PSD2_VERIFIED,
  R.UPSALE_CHANGE_AMOUNT,
];

const checkBEConditionsByRoute = (
  client: Client | undefined,
  application: ClientApplication | undefined,
) => {
  // Allow navigation while data is still loading to avoid a premature redirect
  if (!client || !application) return true;

  const pathName = window.location.pathname;

  switch (pathName) {
    case R.FINANCE:
      return shouldNavigateToFinance(client);
    case R.PHONE_VERIFICATION:
      return !client?.mobilePhoneVerified;
    /*case R.MANUAL_PSD2_CONSENTS:
      return (
        (!application?.resolution || application?.resolution === 'MANUAL') &&
        application?.status === 'OPEN'
      );*/
    case R.MANUAL_REVIEW:
      return (
        application?.resolution === 'MANUAL' &&
        application?.status === 'OPEN' &&
        application?.resolutionDetail !== 'INSUFFICIENT_PSD2_INCOME' &&
        application?.resolutionMessage !== 'ASK_FOR_DOCUMENTS'
      );
    case R.MANUAL_UPLOAD_ID:
      return !client?.hasAttachments;
    case R.REJECTED_OFFER:
      return application?.resolution === 'REJECTED';
    case R.UPSALE:
      return (
        application?.resolution === 'REJECTED' &&
        application?.resolutionDetail === 'UPSALE_OFFER'
      );
    case R.DOWNSALE:
      return (
        application?.resolution === 'REJECTED' &&
        application?.resolutionDetail === 'NEW_OFFER'
      );
    case R.APPROVED:
      return application?.resolution === 'APPROVED';
    case R.UPSALE_CHANGE_AMOUNT:
    case R.LEGACY_LOGIN_ONE_TIME_TOKEN_LOGIN:
    case R.DASHBOARD_REDIRECT:
      return true;
    default:
      if (
        !ROUTES_APPLICATION_NOT_NEEDED.includes(pathName) &&
        application.status !== 'OPEN'
      ) {
        return false;
      }

      return true;
  }
};

// Persists last valid route so the redirect target survives a page refresh
const SESSION_ROUTE_AVAILABLE_KEY = 'last_route_available';

export default function GuardRoute({ children }: Props) {
  const { pathname } = useLocation();
  const isAuthenticated = useSession(
    (state) => state.status === 'authenticated',
  );
  const { data: client, refetch: refetchClient } = useClientQuery();
  const { data: application, refetch: refetchApplication } =
    useApplicationQuery();

  useEffect(() => {
    if (!isAuthenticated) return;

    if (!client) refetchClient();
    if (!application) refetchApplication();
  }, []);

  const isDataReadyToBeChecked = Boolean(client) && Boolean(application);

  if (!isDataReadyToBeChecked) return children;

  const isClientAllowedToVisitRoute = checkBEConditionsByRoute(
    client,
    application,
  );
  const lastRouteAvailable =
    sessionStorage.getItem(SESSION_ROUTE_AVAILABLE_KEY) || pathname;

  if (!isClientAllowedToVisitRoute && pathname !== lastRouteAvailable) {
    // trigger smartlook event to check why the user is redirected
    Smartlook.track('GUARD_ROUTE_INVALID', {
      client: JSON.stringify(client),
      application: JSON.stringify(application),
      pathname,
      lastRouteAvailable,
    });
    // client redirected to the previous route
    return <Navigate to={lastRouteAvailable || '/'} replace />;
  }
  sessionStorage.setItem(SESSION_ROUTE_AVAILABLE_KEY, pathname);

  return children;
}
